import { ofetch } from 'ofetch'

import { router } from '@/plugins/1.router/index'
import { buildImpersonationAuditHeader } from '@/utils/impersonationAudit'

export interface ApiError {
  code: string
  details: string
  target?: string
}

export interface ApiResponse<T> {
  message: string
  data?: T
  error?: ApiError
  ref?: Record<string, any>
}

export const $api = ofetch.create({
  baseURL: import.meta.env.BASE_URL || '',
  async onRequest({ request, options }) {
    const userToken = getValidUserToken()

    // 強制禁用快取 Header
    options.headers = {
      ...options.headers,
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0',
    }

    // 針對 GET 請求加上時間戳記 (Cache Busting)
    if (!options.method || options.method.toUpperCase() === 'GET') {
      options.query = { ...options.query, t: Date.now() }
    }

    if (userToken) {
      options.headers = {
        ...options.headers,
        'X-Token': userToken,
      }
    }

    const audit = buildImpersonationAuditHeader(String(options.method || 'GET'), request)
    if (audit) {
      options.headers = {
        ...options.headers,
        'X-Imp-Audit': audit,
      }
    }
  },
  onResponseError(context) {
    console.error(context)
    const { response } = context
    if (response?.status == 401 || response?.status == 302) {
      $swal({
        position: 'top-end',
        icon: 'question',
        title: '請重新登入',
        text: '你已經登出',
        showConfirmButton: false,
        timer: 1500,
        customClass: {
          confirmButton: 'btn btn-primary',
        },
        buttonsStyling: false,
      })
      // 等待 1.5 秒後執行 doLogout
      setTimeout(() => {
        doLogout(router)
      }, 1500)
    }
    if (response?.status == 403) {
      $swal({
        position: 'top-end',
        icon: 'warning',
        title: '您沒有權限操作此功能',
        showConfirmButton: false,
        timer: 1500,
        customClass: {
          confirmButton: 'btn btn-primary',
        },
        buttonsStyling: false,
      })
    }
    if (response?.status == 406) {
      const message = response?._data?.message ?? ''

      // 身份模擬唯讀阻擋：頁面各自的 catch 會蓋掉這個提示，
      // 因此延遲到所有同步/微任務彈窗之後再顯示，確保使用者看到真正原因。
      if (typeof message === 'string' && message.includes('身份模擬')) {
        setTimeout(() => {
          $swal({
            icon: 'warning',
            title: '身份模擬唯讀模式',
            text: message,
            confirmButtonText: '我知道了',
            confirmButtonColor: '#c62828',
          })
        }, 0)
        return
      }

      $swal({
        position: 'top-end',
        icon: 'warning',
        title: `${message}`,
        showConfirmButton: false,
        timer: 1500,
        customClass: {
          confirmButton: 'btn btn-primary',
        },
        buttonsStyling: false,
      })
    }
  },
})

export const useJwt = async (): Promise<string | null> => {
  try {
    const { data: jwt, execute: getJwt } = await useApi<any>(
      createUrl('/tca/file/get', {})
    );
    return jwt;
  } catch (error) {
    console.error('Error fetching JWT:', error);
    return null; // 返回 null 表示出錯
  }
};
